import React, { useState, useEffect } from 'react';
import { useSetup } from '../context/SetupContext';
import { loadFromIndexedDB, saveToIndexedDB } from '../utils/IndexedDBUtils';
import "./FilterBar.css";

const FilterBar = ({ onFilterChange , onResetFilters, reset }) => {
  const { labels, labelTypes } = useSetup();
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    source: '',
    dynamicFilters: Object.keys(labelTypes).reduce((acc, labelType) => {
      acc[labelType] = [];
      return acc;
    }, {}),
  });
  const [collapsedSections, setCollapsedSections] = useState({});
  // State to track active special buttons (Known/Not neutral)
  // const [activeSpecialButtons, setActiveSpecialButtons] = useState({
  //   Gender: false,
  //   Age: false,
  //   Sentiment: false
  // });

  // Load filters and collapsed sections from IndexedDB
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load filters
        const storedFilters = await loadFromIndexedDB('appSettings', 'filters');
        const initialFilters = storedFilters || {
          source: '',
          dynamicFilters: Object.keys(labelTypes).reduce((acc, labelType) => {
            acc[labelType] = [];
            return acc;
          }, {}),
        };
        setFilters(initialFilters);
        
        // Load collapsed sections
        const storedCollapsedSections = await loadFromIndexedDB('appSettings', 'collapsedSections');
        setCollapsedSections(storedCollapsedSections || {});
        
        setIsLoading(false);
        onFilterChange(initialFilters); // Apply filters on mount
      } catch (error) {
        console.error('Erreur lors du chargement des filtres:', error);
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  useEffect(() => {
    if (reset) {
      resetFilters(); // Reset filters if reset prop is true
      onResetFilters(); // Notify parent about reset
    }
  }, [reset, onResetFilters]);

  // Save filters to IndexedDB when they change
  useEffect(() => {
    if (!isLoading) {
      saveToIndexedDB('appSettings', filters, 'filters');
    }
  }, [filters, isLoading]);

  // Save collapsed sections to IndexedDB when they change
  useEffect(() => {
    if (!isLoading) {
      saveToIndexedDB('appSettings', collapsedSections, 'collapsedSections');
    }
  }, [collapsedSections, isLoading]);

  const handleLabelClick = (labelType, labelName) => {
    setFilters((prevFilters) => {
      const currentLabels = prevFilters.dynamicFilters[labelType] || [];
      const newLabels = currentLabels.includes(labelName)
        ? currentLabels.filter((label) => label !== labelName) // Deselect if already selected
        : [...currentLabels, labelName]; // Select if not selected

      const updatedFilters = {
        ...prevFilters,
        dynamicFilters: {
          ...prevFilters.dynamicFilters,
          [labelType]: newLabels,
        },
      };

      // Call onFilterChange inside the click handler itself
      onFilterChange(updatedFilters);

      return updatedFilters;
    });
  };

  const resetFilters = () => {
    const defaultFilters = {
      source: '',
      dynamicFilters: Object.keys(labelTypes).reduce((acc, labelType) => {
        acc[labelType] = [];
        return acc;
      }, {}),
    };

    setFilters(defaultFilters); // Reset filters to their default state
    onFilterChange(defaultFilters); // Notify parent about filter reset
  };

  // Count active filters for each labelType
  const getActiveFilterCount = (labelType) => {
    return filters.dynamicFilters[labelType]?.length || 0;
  };

  // Function to handle click on "Known" or "Not neutral"
  const handleKnownClick = (labelType) => {
    setFilters((prevFilters) => {
      // Get all available labels for this type
      const allLabels = labels[labelType]?.map(label => label.name) || [];

      // Determine labels to exclude
      let labelsToExclude = [];
      if (labelType === 'Gender') {
        labelsToExclude = ['Unknown Gender'];
      } else if (labelType === 'Age') {
        labelsToExclude = ['Unknown Age'];
      } else if (labelType === 'Sentiment') {
        labelsToExclude = ['neutral'];
      }
      
      // Filtrer les labels à sélectionner (tous sauf ceux à exclure et "NO_LABELS")
      const labelsToSelect = allLabels.filter(
        label => !labelsToExclude.includes(label) && label !== 'NO_LABELS'
      );

      // if all labels are already selected, deselect them all
      const currentLabels = prevFilters.dynamicFilters[labelType] || [];
      const allAlreadySelected = labelsToSelect.every(label => currentLabels.includes(label));
      const newLabels = allAlreadySelected ? [] : labelsToSelect;
      // Update special button state
      // setActiveSpecialButtons(prev => ({
      //   ...prev,
      //   [labelType]: !allAlreadySelected
      // }));
      
      const updatedFilters = {
        ...prevFilters,
        dynamicFilters: {
          ...prevFilters.dynamicFilters,
          [labelType]: newLabels,
        },
      };

      // Notify parent about filter change
      onFilterChange(updatedFilters);
      
      return updatedFilters;
    });
  };

  if (isLoading) {
    return <div className="filter-bar">Loading filters...</div>;
  }

  return (
    <div className="filter-bar">
      <div className="filter-header">
        <i className="fa fa-filter" aria-hidden="true"></i> Filter
        <button className="reset-filters-button" onClick={resetFilters}>
          Reset
        </button>
      </div>
      {Object.keys(labelTypes).map((labelType) => {
        const activeCount = getActiveFilterCount(labelType);

        return (
          <div key={labelType}>
            <button
              className={`collapsible ${collapsedSections[labelType] ? 'active' : ''}`}
              onClick={() =>
                setCollapsedSections((prev) => ({
                  // Collapse all other sections, expand the clicked one
                  ...Object.keys(labelTypes).reduce((acc, key) => {
                    acc[key] = false; // Collapse all sections
                    return acc;
                  }, {}),
                  [labelType]: !prev[labelType], // Toggle the clicked section
                }))
              }
            >
              {labelType}
              {activeCount > 0 && <span className="active-marker"> ({activeCount})</span>}
            </button>

            <div className={`content ${collapsedSections[labelType] ? 'active' : ''}`}>
              {/* Bouton Empty */}
              <button
                className={`filter-button no-labels ${filters.dynamicFilters[labelType]?.includes('NO_LABELS') ? 'active' : ''}`}
                onClick={() => handleLabelClick(labelType, 'NO_LABELS')}
              >
                Empty
              </button>

              {/* Boutons de label existants */}
              {labels[labelType]?.map((label) => (
                <button
                  key={label.name}
                  className={`filter-button ${filters.dynamicFilters[labelType]?.includes(label.name) ? 'active' : ''}`}
                  onClick={() => handleLabelClick(labelType, label.name)}
                >
                  {label.name}
                </button>
              ))}
                             
              {/* Ajouter les boutons Known/Not neutral pour les types spécifiques */}
              {(labelType === 'Gender' || labelType === 'Age') && (
                <button className={`filter-button`} onClick={() => handleKnownClick(labelType)}>Exclude unknown</button>
              )}
              {labelType === 'Sentiment' && (
                <button className={`filter-button`} onClick={() => handleKnownClick(labelType)}>Exclude neutral</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FilterBar;
